// apps/client/src/hooks/useYoYComparison.ts
// Hook to calculate Year-over-Year growth percentages for dashboard metrics

import { useMemo } from 'react';

interface YoYMetrics {
  upfront: number;
  trail: number;
  loanBook: number;
  lineOfTrail: number;
  settlements?: number;
}

interface YoYGrowth {
  upfront: number | null;
  trail: number | null;
  loanBook: number | null;
  lineOfTrail: number | null;
  settlements: number | null;
}

/**
 * Calculate Year-over-Year growth percentage. The ONE clamp for every
 * "vs last year" badge on every dashboard.
 *
 * @param current - Current year value
 * @param previous - Previous year value
 * @returns Growth percentage, or null when there is no meaningful comparison
 *
 * Examples:
 *   calculateGrowth(120000, 100000) -> 20.0 (20% growth)
 *   calculateGrowth(80000, 100000) -> -20.0 (20% decline)
 *   calculateGrowth(100, 0) -> null (zero previous = no meaningful comparison)
 *   calculateGrowth(0, 100) -> -100.0 (100% decline to zero)
 *   calculateGrowth(400000, 1000) -> 39900 (huge but real; the CARD caps the display at >999%)
 */
export const calculateGrowth = (current: number, previous: number): number | null => {
  // No data at all
  if (previous === 0 && current === 0) {
    return null;
  }

  // Zero previous = no meaningful comparison (snapshot missing or pre-onboarding period)
  if (previous === 0) {
    return null;
  }

  // Calculate percentage change: ((current - previous) / |previous|) * 100
  const growth = ((current - previous) / Math.abs(previous)) * 100;

  // NaN / Infinity are not a comparison and must not reach a badge.
  //
  // A huge-but-finite growth is NOT clamped here, deliberately. `null` renders
  // the literal "No prior year data", and on a tiny-but-nonzero prior-year base
  // (a tenant whose book starts mid-window) prior-year data plainly DOES exist:
  // saying otherwise is a false statement on a regulated financial surface. The
  // display cap belongs to the card, which says ">999%" and is true. One clamp
  // still, just on the truthful side of the boundary: StatCardWithGrowth is the
  // single place the ceiling is applied, and every caller goes through it.
  if (!Number.isFinite(growth)) {
    return null;
  }

  return growth;
};

/**
 * Hook to calculate Year-over-Year growth for all dashboard metrics
 *
 * Used by: DashboardPage, OfficeDashboardPage
 *
 * @param currentYear - Metrics for selected year
 * @param previousYear - Metrics for previous year
 * @returns Object with growth percentages (null if insufficient data)
 */
export function useYoYComparison(
  currentYear: YoYMetrics | null,
  previousYear: YoYMetrics | null
): YoYGrowth {
  return useMemo(() => {
    // If either year is missing, return null for all metrics
    if (!currentYear || !previousYear) {
      return {
        upfront: null,
        trail: null,
        loanBook: null,
        lineOfTrail: null,
        settlements: null,
      };
    }

    return {
      upfront: calculateGrowth(currentYear.upfront, previousYear.upfront),
      trail: calculateGrowth(currentYear.trail, previousYear.trail),
      loanBook: calculateGrowth(currentYear.loanBook, previousYear.loanBook),
      lineOfTrail: calculateGrowth(currentYear.lineOfTrail, previousYear.lineOfTrail),
      settlements: currentYear.settlements !== undefined && previousYear.settlements !== undefined
        ? calculateGrowth(currentYear.settlements, previousYear.settlements)
        : null,
    };
  }, [currentYear, previousYear]);
}
